import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import type { IGlobalConfig } from 'types';

import enCommon from './i18n/en/common.json';
import enDialogs from './i18n/en/dialogs.json';
import enFormsFinance from './i18n/en/forms.finance.json';
import enFormsRegistration from './i18n/en/forms.registration.json';
import enFormsVisa from './i18n/en/forms.visa.json';
import enNavigation from './i18n/en/navigation.json';
import enTables from './i18n/en/tables.json';
import enToasts from './i18n/en/toasts.json';
import cyCommon from './i18n/cy/common.json';
import cyDialogs from './i18n/cy/dialogs.json';
import cyFormsFinance from './i18n/cy/forms.finance.json';
import cyFormsRegistration from './i18n/cy/forms.registration.json';
import cyFormsVisa from './i18n/cy/forms.visa.json';
import cyNavigation from './i18n/cy/navigation.json';
import cyTables from './i18n/cy/tables.json';
import cyToasts from './i18n/cy/toasts.json';
import auRegion from './i18n/regions/au.json';

// Per-uni list of supported languages + vocabulary overrides come from the
// ENV_VARIABLES DDB row, surfaced into window.globalConfig by the env-variables
// Lambda@Edge. Absent fields are treated as English-only so existing tenants
// behave unchanged.
//
// translationOverrides format:
//   { [language]: { '<namespace>:<key.path>': 'override text' } }
// e.g. { en: { 'common:cancel': "Don't Save" } }

export async function initI18n(globalConfig: IGlobalConfig | undefined): Promise<unknown> {
  const supported =
    Array.isArray(globalConfig?.languages) && globalConfig.languages.length > 0 ? globalConfig.languages : ['en'];
  const defaultLanguage = globalConfig?.defaultLanguage ?? 'en';

  // eslint-disable-next-line import/no-named-as-default-member
  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: defaultLanguage,
      supportedLngs: supported,
      nonExplicitSupportedLngs: true,
      ns: [
        'common',
        'dialogs',
        'forms.finance',
        'forms.registration',
        'forms.visa',
        'navigation',
        'tables',
        'toasts',
      ],
      defaultNS: 'common',
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'i18n.language',
        caches: ['localStorage'],
      },
      interpolation: { escapeValue: false },
      resources: {
        en: {
          common: enCommon,
          dialogs: enDialogs,
          'forms.finance': enFormsFinance,
          'forms.registration': enFormsRegistration,
          'forms.visa': enFormsVisa,
          navigation: enNavigation,
          tables: enTables,
          toasts: enToasts,
        },
        cy: {
          common: cyCommon,
          dialogs: cyDialogs,
          'forms.finance': cyFormsFinance,
          'forms.registration': cyFormsRegistration,
          'forms.visa': cyFormsVisa,
          navigation: cyNavigation,
          tables: cyTables,
          toasts: cyToasts,
        },
      },
    });

  // Layer order (each later layer wins on key collision):
  //   1. Base bundles (loaded above via init resources)
  //   2. Region pack — uni-jurisdiction-driven (CAS↔CoE, UKVI↔DHA)
  //   3. Per-uni translationOverrides — bespoke wording for one tenant
  applyRegionPack(globalConfig?.jurisdiction);
  applyTranslationOverrides(globalConfig?.translationOverrides);
  return undefined;
}

// Region packs swap UK-default vocabulary for the regional equivalent — e.g.
// AU unis read "CoE" everywhere "CAS" appears in the base bundles. The pack
// shape is the same as translationOverrides:
//   { [lang]: { '<namespace>:<key.path>': '<override>' } }
function applyRegionPack(jurisdiction: string | undefined): void {
  const pack = regionPackFor(jurisdiction);
  if (!pack) return;
  applyOverrideMap(pack);
}

function regionPackFor(jurisdiction: string | undefined): Record<string, Record<string, string>> | undefined {
  switch (jurisdiction) {
    case 'AU':
      return auRegion as Record<string, Record<string, string>>;
    default:
      return undefined; // UK + everything else uses base bundles as-is
  }
}

// Per-uni overrides are layered on top of the bundled translations so the
// codemod-generated en.json bundles stay tenant-agnostic. addResource
// shallow-merges into the existing keys, so only the overridden keys change.
function applyTranslationOverrides(
  overrides: { [language: string]: { [namespacedKey: string]: string } } | undefined,
): void {
  if (!overrides) return;
  applyOverrideMap(overrides);
}

function applyOverrideMap(map: { [language: string]: { [namespacedKey: string]: string } }): void {
  for (const [language, entries] of Object.entries(map)) {
    for (const [namespacedKey, value] of Object.entries(entries)) {
      const colon = namespacedKey.indexOf(':');
      if (colon <= 0) continue;
      const namespace = namespacedKey.slice(0, colon);
      const key = namespacedKey.slice(colon + 1);
      // eslint-disable-next-line import/no-named-as-default-member
      i18next.addResource(language, namespace, key, value);
    }
  }
}

export default i18next;
